<template>
  <div class="list-page">
    <div class="detail-banner">
      <div class="container">
        <h1>精选产品</h1>
        <p class="pd-banner-sub">提供一级 / 二级 / 三级分类联动筛选，快速定位所需产品</p>
      </div>
    </div>
    <section class="section">
      <div class="container">
        <span class="detail-back" @click="$goBack()">‹ 返回</span>

        <!-- 一级分类 -->
        <div class="cat-block" v-if="firstTypes.length">
          <div class="cat-head">
            <span class="cat-label">一级分类</span>
          </div>
          <div class="cat-tabs">
            <button
              v-for="t in firstTypes"
              :key="t.id"
              class="cat-tab"
              :class="{ active: activeFirstId === t.id }"
              @click="selectFirst(t.id)"
            >{{ t.name }}</button>
          </div>
        </div>
        <div class="empty-block" v-else-if="!firstLoading">暂无产品分类</div>
        <div class="empty-block" v-else>加载中…</div>

        <!-- 二级分类（选了一级后展示） -->
        <div class="cat-block" v-if="activeFirstId && secondTypes.length">
          <div class="cat-head">
            <span class="cat-label">二级分类</span>
          </div>
          <div class="cat-tabs">
            <button
              class="cat-tab"
              :class="{ active: activeSecondId === '' }"
              @click="selectSecond('')"
            >全部</button>
            <button
              v-for="s in secondTypes"
              :key="s.id"
              class="cat-tab"
              :class="{ active: activeSecondId === s.id }"
              @click="selectSecond(s.id)"
            >{{ s.name }}</button>
          </div>
        </div>

        <!-- 三级分类（选了二级后展示） -->
        <div class="cat-block" v-if="activeSecondId && thirdTypes.length">
          <div class="cat-head">
            <span class="cat-label">三级分类</span>
          </div>
          <div class="cat-tabs">
            <button
              class="cat-tab"
              :class="{ active: activeThirdId === '' }"
              @click="selectThird('')"
            >全部</button>
            <button
              v-for="th in thirdTypes"
              :key="th.id"
              class="cat-tab"
              :class="{ active: activeThirdId === th.id }"
              @click="selectThird(th.id)"
            >{{ th.name }}</button>
          </div>
        </div>

        <!-- 当前筛选路径 + 数量 -->
        <div class="block-title" v-if="activeFirstId">
          <span class="bt-main">{{ activeFirstName }}</span>
          <span class="bt-sub" v-if="activeSecondName"> / {{ activeSecondName }}</span>
          <span class="bt-sub" v-if="activeThirdName"> / {{ activeThirdName }}</span>
          <span class="bt-count">共 {{ productTotal }} 件</span>
        </div>

        <!-- 产品网格 -->
        <div class="card-grid" v-if="productRows.length">
          <div
            v-for="item in productRows"
            :key="item.id"
            class="card"
            @click="$router.push('/product/' + item.id)"
          >
            <div class="card-cover product-cover">
              <span class="cover-tag" v-if="item.typeName">{{ item.typeName }}</span>
              <img v-if="item.cover" :src="item.cover" :alt="item.name" @error="onImgError" />
              <span v-else class="cover-letter">{{ item.name.slice(0, 1) }}</span>
            </div>
            <div class="card-body">
              <div class="card-title">{{ item.name }}</div>
              <div class="card-desc">{{ item.summary }}</div>
              <div class="card-tags" v-if="item.secondTypeName || item.thirdTypeName">
                <span class="card-tag" v-if="item.secondTypeName">{{ item.secondTypeName }}</span>
                <span class="card-tag" v-if="item.thirdTypeName">{{ item.thirdTypeName }}</span>
              </div>
              <div class="card-source" v-if="item.sourcePlace">来源地：{{ item.sourcePlace }}</div>
              <div class="card-detail-btn">查看详情</div>
            </div>
          </div>
        </div>

        <div class="empty-block" v-else-if="productLoading">加载中…</div>
        <div class="empty-block" v-else>该分类暂无产品</div>

        <!-- 产品分页（服务端分页） -->
        <div class="pager" v-if="productTotalPages > 1">
          <button class="pg-btn" :disabled="productPage <= 1" @click="goProductPage(productPage - 1)">‹ 上一页</button>
          <button
            v-for="p in productPages"
            :key="p"
            class="pg-num"
            :class="{ active: p === productPage }"
            @click="goProductPage(p)"
          >{{ p }}</button>
          <button class="pg-btn" :disabled="productPage >= productTotalPages" @click="goProductPage(productPage + 1)">下一页 ›</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  fetchProductFirstTypes,
  fetchProductTypeTree,
  fetchProductsByType
} from '../api/home'

export default {
  name: 'ProductsList',
  data () {
    return {
      // 一级类型
      firstTypes: [],
      firstLoading: true,
      activeFirstId: '',
      // 二级类型（当前一级下的二级）
      secondTypes: [],
      activeSecondId: '',
      // 三级类型（当前二级下的三级）
      thirdTypes: [],
      activeThirdId: '',
      // 产品列表（按三级联动筛选，服务端分页）
      productRows: [],
      productTotal: 0,
      productPage: 1,
      productPageSize: 8,
      productLoading: true
    }
  },
  computed: {
    activeFirstName () {
      const t = this.firstTypes.find(x => x.id === this.activeFirstId)
      return t ? t.name : ''
    },
    activeSecondName () {
      if (!this.activeSecondId) return ''
      const s = this.secondTypes.find(x => x.id === this.activeSecondId)
      return s ? s.name : ''
    },
    activeThirdName () {
      if (!this.activeThirdId) return ''
      const t = this.thirdTypes.find(x => x.id === this.activeThirdId)
      return t ? t.name : ''
    },
    productTotalPages () {
      return Math.max(1, Math.ceil(this.productTotal / this.productPageSize))
    },
    productPages () {
      return this.buildPages(this.productPage, this.productTotalPages)
    }
  },
  created () {
    this.loadFirstTypes()
  },
  methods: {
    buildPages (cur, tp) {
      if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1)
      const set = new Set([1, tp, cur, cur - 1, cur + 1, cur - 2, cur + 2])
      const arr = [...set].filter(p => p >= 1 && p <= tp).sort((a, b) => a - b)
      const out = []
      let prev = 0
      for (const p of arr) {
        if (p - prev > 1) out.push('...')
        out.push(p)
        prev = p
      }
      return out
    },
    async loadFirstTypes () {
      this.firstLoading = true
      try {
        const res = await fetchProductFirstTypes({ pageNum: 1, pageSize: 100 })
        this.firstTypes = res.rows || []
        if (this.firstTypes.length) {
          this.selectFirst(this.firstTypes[0].id)
        }
      } catch (e) {
        this.firstTypes = []
        this.$toast.show({ message: (e && e.message) || '分类加载失败', type: 'error' })
      } finally {
        this.firstLoading = false
      }
    },
    async selectFirst (id) {
      if (this.activeFirstId === id) return
      this.activeFirstId = id
      this.activeSecondId = ''
      this.activeThirdId = ''
      this.thirdTypes = []
      this.productPage = 1
      // 加载该一级下的二三级树，并拉取该一级产品
      await this.loadTree(id)
      this.loadProducts()
    },
    async loadTree (id) {
      try {
        const tree = await fetchProductTypeTree(id)
        this.secondTypes = tree.children || []
      } catch (e) {
        this.secondTypes = []
      }
    },
    selectSecond (id) {
      if (this.activeSecondId === id) return
      this.activeSecondId = id
      this.activeThirdId = ''
      this.thirdTypes = []
      // 选择了具体二级时，展开其下的三级
      if (id) {
        const sec = this.secondTypes.find(x => x.id === id)
        this.thirdTypes = (sec && sec.children) || []
      }
      this.productPage = 1
      this.loadProducts()
    },
    selectThird (id) {
      if (this.activeThirdId === id) return
      this.activeThirdId = id
      this.productPage = 1
      this.loadProducts()
    },
    async loadProducts () {
      if (!this.activeFirstId) return
      this.productLoading = true
      try {
        const res = await fetchProductsByType({
          typeId: this.activeFirstId,
          secondTypeId: this.activeSecondId || undefined,
          thirdTypeId: this.activeThirdId || undefined,
          pageNum: this.productPage,
          pageSize: this.productPageSize
        })
        this.productRows = res.rows || []
        this.productTotal = res.total || 0
      } catch (e) {
        this.productRows = []
        this.productTotal = 0
        this.$toast.show({ message: (e && e.message) || '产品加载失败', type: 'error' })
      } finally {
        this.productLoading = false
      }
    },
    goProductPage (p) {
      if (p < 1 || p > this.productTotalPages || p === this.productPage) return
      this.productPage = p
      this.loadProducts()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    onImgError (e) {
      e.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
/* 分类联动面板 */
.cat-block {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 18px 22px;
  box-shadow: var(--shadow);
  margin-bottom: 18px;
}
.cat-head {
  margin-bottom: 12px;
}
.cat-label {
  font-size: 14px;
  font-weight: 800;
  color: var(--text-main);
  position: relative;
  padding-left: 14px;
}
.cat-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  bottom: 2px;
  width: 4px;
  border-radius: 3px;
  background: linear-gradient(180deg, var(--primary-light), var(--primary));
}
.cat-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.cat-tab {
  border: 1px solid var(--border);
  background: var(--blue-soft-2);
  color: var(--text-main);
  padding: 8px 18px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.22s ease;
}
.cat-tab:hover {
  border-color: var(--primary);
  color: var(--primary);
}
.cat-tab.active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}

/* 当前筛选路径标题 */
.block-title {
  margin: 14px 0 22px;
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.bt-main {
  font-size: 19px;
  font-weight: 800;
  color: var(--text-main);
}
.bt-sub {
  font-size: 15px;
  font-weight: 700;
  color: var(--primary);
}
.bt-count {
  margin-left: auto;
  font-size: 13px;
  color: var(--text-sub);
}

/* 产品卡片网格 */
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 22px;
}
.card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;
}
.card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-hover);
  border-color: var(--primary);
}
.card-cover {
  position: relative;
  height: 170px;
  background: linear-gradient(135deg, var(--primary-light), var(--primary));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.card-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
/* 精选产品封面：等比伸缩容器，图片完整展示不裁切 */
.product-cover {
  height: auto;
  aspect-ratio: 12 / 5;
  background: #f5f7fb;
}
.product-cover img {
  object-fit: contain;
}
.cover-tag {
  position: absolute;
  left: 10px;
  top: 10px;
  background: var(--primary);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
  z-index: 2;
}
.cover-letter {
  color: #fff;
  font-size: 56px;
  font-weight: 800;
}
.card-body {
  padding: 16px 18px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.card-title {
  font-size: 17px;
  font-weight: 800;
  color: var(--text-main);
  margin-bottom: 8px;
}
.card-desc {
  font-size: 13.5px;
  color: var(--text-sub);
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 10px;
}
.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.card-tag {
  background: var(--blue-soft);
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 14px;
}
.card-source {
  font-size: 12.5px;
  color: var(--text-sub);
  margin-bottom: 14px;
}
.card-detail-btn {
  margin: auto -18px -20px;
  text-align: center;
  background: var(--primary);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  padding: 12px 0;
  border-radius: 0;
  letter-spacing: 1px;
  transition: background 0.22s ease;
}
.card:hover .card-detail-btn {
  background: var(--primary-dark);
}

/* 分页器 */
.pager {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 30px;
  flex-wrap: wrap;
}
.pg-btn,
.pg-num {
  min-width: 38px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--border);
  background: #fff;
  color: var(--text-main);
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.pg-btn:hover:not(:disabled),
.pg-num:hover {
  border-color: var(--primary);
  color: var(--primary);
}
.pg-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.pg-num.active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}
.pager :not(button) {
  color: var(--text-sub);
  padding: 0 4px;
}
.empty-block {
  text-align: center;
  color: var(--text-sub);
  padding: 40px 0;
}
</style>
